import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import MarketingService, {
    type MemberAcquisition,
    type Partner,
    type Touch,
} from '../../services/marketing/marketingService.ts';
import { errorMessage, fmtDateTime } from '../../pages/marketing/marketingShared.ts';

const service = new MarketingService();

const headCellSx = {
    paddingLeft: 'var(--space-24)',
    py: 'var(--space-16)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
};

const bodyCellSx = {
    py: 'var(--space-16)',
    paddingLeft: 'var(--space-24)',
    fontSize: 'var(--text-xs)',
};

const shellSx = {
    border: '1px solid var(--color-secondary)',
    borderRadius: 'var(--radius-6)',
    overflow: 'hidden',
    width: '100%',
};

/** "google / cpc · summer-qr" - the touch in one line, or a partner when that is what it was. */
const describeTouch = (touch: Touch, unknownCode: string): string => {
    const parts: string[] = [];
    const source = [touch.source, touch.medium].filter(Boolean).join(' / ');
    if (source) parts.push(source);
    if (touch.campaign) parts.push(touch.campaign);
    if (touch.partnerName || touch.partnerCode) {
        parts.push(touch.partnerName ? `${touch.partnerName} (${touch.partnerCode ?? ''})` : `${touch.partnerCode} (${unknownCode})`);
    }
    if (!parts.length && touch.referrerHost) parts.push(touch.referrerHost);
    return parts.join(' · ') || '—';
};

const dash = (v: string | null | undefined) => (v ? v : '—');

/**
 * "Came from and consent" on a customer's page: the first link, QR code or
 * advert they arrived through, the partner they are counted for (which an
 * admin can set or clear here), every touch, and every consent record - the
 * full audit trail, newest first. Admins only.
 */
const MemberAcquisitionPanel: React.FC<{ customerId: number }> = ({ customerId }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const [data, setData] = useState<MemberAcquisition | null>(null);
    const [failed, setFailed] = useState(false);
    const [partners, setPartners] = useState<Partner[] | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setFailed(false);
        return service.memberAcquisition(customerId)
            .then(setData)
            .catch(() => setFailed(true));
    }, [customerId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        service.partners().then(setPartners).catch(() => setPartners([]));
    }, []);

    const choose = async (partner: Partner | null) => {
        if ((partner?.id ?? null) === (data?.partner?.id ?? null)) return;
        setSaving(true);
        try {
            await service.setMemberPartner(customerId, partner?.id ?? null);
            notify(partner ? t('members.acquisition.partnerSet', { name: partner.name }) : t('members.acquisition.partnerCleared'),
                { type: 'success' });
            await load();
        } catch (e) {
            notify(errorMessage(e, t('common.saveFailed')), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // The current partner as an option even while the list loads, or if it is
    // inactive and somehow missing from it.
    const selected = data?.partner
        ? (partners?.find((p) => p.id === data.partner!.id) ?? ({ ...data.partner, isActive: true } as Partner))
        : null;

    const touchColumns = [
        t('members.acquisition.date'),
        t('members.acquisition.channel'),
        t('members.acquisition.kind'),
        t('members.acquisition.sourceMedium'),
        t('members.acquisition.campaign'),
        t('members.acquisition.partner'),
        t('members.acquisition.referrer'),
        t('members.acquisition.landing'),
    ];
    const consentColumns = [
        t('members.acquisition.date'),
        t('members.acquisition.consentType'),
        t('members.acquisition.granted'),
        t('members.acquisition.policyVersion'),
        t('members.acquisition.channel'),
        t('members.acquisition.appVersion'),
    ];

    return (
        <Box data-member-acquisition="1" sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 'var(--text-xxl)', mb: 'var(--space-16)' }}>
                {t('members.acquisition.title')}
            </Typography>

            {!data && !failed && <CircularProgress size={20} />}
            {failed && (
                <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>
                    {t('members.acquisition.loadFailed')}
                </Typography>
            )}

            {data && (
                <Stack spacing={3}>
                    <Box sx={{ ...shellSx, p: 'var(--space-24)' }}>
                        <Stack spacing={2}>
                            <Box>
                                <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 700 }}>
                                    {t('members.acquisition.firstTouch')}
                                </Typography>
                                <Typography sx={{ fontSize: 'var(--text-xs)' }}>
                                    {data.firstTouch
                                        ? `${describeTouch(data.firstTouch, t('members.acquisition.unknownCode'))} · ${dash(data.firstTouch.channel)} · ${fmtDateTime(data.firstTouch.createdAt)}`
                                        : t('members.acquisition.noFirstTouch')}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 700, mb: 1 }}>
                                    {t('members.acquisition.partner')}
                                </Typography>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                    <Autocomplete
                                        size="small"
                                        sx={{ minWidth: 320 }}
                                        options={partners ?? []}
                                        loading={!partners}
                                        disabled={saving}
                                        value={selected}
                                        getOptionLabel={(p) => `${p.name} (${p.code})`}
                                        getOptionDisabled={(p) => !p.isActive && p.id !== data.partner?.id}
                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                        onChange={(_, p) => choose(p)}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder={t('members.acquisition.noPartner')} />
                                        )}
                                    />
                                    {saving && <CircularProgress size={18} />}
                                    {data.partner && (
                                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                            {t('members.acquisition.attributed', {
                                                how: data.partnerAttribution
                                                    ? t(`members.acquisition.attribution.${data.partnerAttribution}`, data.partnerAttribution)
                                                    : '—',
                                                date: fmtDateTime(data.partnerAttributedAt),
                                            })}
                                            {' · '}
                                            <Box component={Link} to={`/marketing/partner-statement?partnerId=${data.partner.id}`}
                                                sx={{ color: 'var(--color-primary)' }}>
                                                {t('marketing.partners.openStatement')}
                                            </Box>
                                        </Typography>
                                    )}
                                </Stack>
                                <Typography sx={{ mt: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                    {t('members.acquisition.partnerHelp')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('members.acquisition.touches')}</Typography>
                        <Box sx={shellSx}>
                            {data.touches.length === 0 ? (
                                <Typography sx={{ p: 'var(--space-24)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                    {t('members.acquisition.noTouches')}
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table sx={{ minWidth: 900 }}>
                                        <TableHead>
                                            <TableRow>{touchColumns.map((c) => <TableCell key={c} sx={headCellSx}>{c}</TableCell>)}</TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.touches.map((tc) => (
                                                <TableRow key={tc.id}>
                                                    <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{fmtDateTime(tc.createdAt)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(tc.channel)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(tc.kind)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{[tc.source, tc.medium].filter(Boolean).join(' / ') || '—'}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{[tc.campaign, tc.content].filter(Boolean).join(' · ') || '—'}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        {tc.partnerName ? `${tc.partnerName} (${tc.partnerCode ?? ''})` : (tc.partnerCode ? `${tc.partnerCode} (${t('members.acquisition.unknownCode')})` : '—')}
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(tc.referrerHost)}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-all' }}>{dash(tc.landingPath)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('members.acquisition.consents')}</Typography>
                        <Box sx={shellSx}>
                            {data.consents.length === 0 ? (
                                <Typography sx={{ p: 'var(--space-24)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                    {t('members.acquisition.noConsents')}
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table sx={{ minWidth: 720 }}>
                                        <TableHead>
                                            <TableRow>{consentColumns.map((c) => <TableCell key={c} sx={headCellSx}>{c}</TableCell>)}</TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.consents.map((c, i) => (
                                                <TableRow key={`${c.type}-${c.createdAt}-${i}`}>
                                                    <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{fmtDateTime(c.createdAt)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{t(`members.acquisition.consentTypes.${c.type}`, { defaultValue: c.type })}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip size="small" variant="outlined"
                                                            color={c.granted ? 'success' : 'default'}
                                                            label={c.granted ? t('members.acquisition.yes') : t('members.acquisition.no')} />
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(c.policyVersion)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(c.channel)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{dash(c.appVersion)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                        <Typography sx={{ mt: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                            {t('members.acquisition.consentsHelp')}
                        </Typography>
                    </Box>
                </Stack>
            )}
        </Box>
    );
};

export default MemberAcquisitionPanel;
